import React from "react";
import {
  Container,
  Divider,
  Grid,
  GridItem,
  SimpleGrid,
  Spacer,
  Text
} from "@chakra-ui/react";
import { add, format } from "date-fns";

import { ReactComponent as Logo } from "assets/logo.svg";
import PngLogo from "assets/UT_Dallas_Black.png";
import colors from "theme/foundations/colors.style";
import { Invoice } from "types/invoice";
import { padNum, prettyMoney } from "utils";

const Header: React.FC = () => (
  <SimpleGrid maxH="10mm">
    <Logo />
  </SimpleGrid>
);

interface PageProps {
  children: React.ReactNode;
  hideHeader?: boolean;
}
const Page: React.FC<PageProps> = ({ children, hideHeader }: PageProps) => (
  <Container backgroundColor={colors.white} color="black" p="10">
    {hideHeader ? null : <Header />}
    {children}
  </Container>
);

interface Props {
  invoice: Invoice;
}

// pass invoiced organization
const PDF: React.FC<Props> = ({
  invoice: {
    organization: { name, displayName, rate },
    invoice_num,
    amount,
    due,
    line_items,
    start_date,
    end_date
  }
}: Props) => (
  <Container id="toPrint" maxW="210mm" p={0}>
    <Page hideHeader>
      <Grid
        gap={3}
        templateColumns="1fr 2fr"
        templateRows="repeat(4, fit-content(100%))"
      >
        <GridItem>
          <img src={PngLogo} />
        </GridItem>
        <SimpleGrid
          columns={2}
          alignItems="flex-end"
          templateColumns="fit-content(100%) 1fr"
        >
          <Text fontWeight="bold">Invoice Number:</Text>
          <Text justifySelf="flex-end">
            {`${name}_${padNum(invoice_num, 5)}`}
          </Text>
          <Text fontWeight="bold">Invoice Date:</Text>
          <Text justifySelf="flex-end">
            {format(add(new Date(end_date), { days: 1 }), "MM/dd/yyyy")}
          </Text>
          <Text fontWeight="bold">Billing Period:</Text>
          <Text justifySelf="flex-end">{`${format(
            new Date(start_date),
            "MM/dd/yyyy"
          )} to ${format(new Date(end_date), "MM/dd/yyyy")}`}</Text>
          <Text fontWeight="bold">Page:</Text>
          <Text justifySelf="flex-end">1 of 1</Text>
        </SimpleGrid>
        <GridItem colSpan={2} margin="auto">
          <Text fontSize="2xl" fontWeight="bold" justifySelf="center">
            Charisma Software Licensing Invoice
          </Text>
        </GridItem>
        <Spacer />
        <SimpleGrid columns={2} alignContent="flex-start">
          <Text fontWeight="bold">Customer:</Text>
          <Text justifySelf="flex-end">{displayName}</Text>
          <Text fontWeight="bold">Payment Terms:</Text>
          <Text justifySelf="flex-end">Upon Receipt</Text>
          <Text fontWeight="bold">Due Date:</Text>
          <Text justifySelf="flex-end">
            {format(new Date(due), "MM/dd/yyyy")}
          </Text>
        </SimpleGrid>
        <Spacer />
        <SimpleGrid gridColumn={2} columns={2} fontWeight="bold">
          <Text>AMOUNT DUE:</Text>
          <Text justifySelf="flex-end">{prettyMoney(amount)}</Text>
        </SimpleGrid>
      </Grid>
      <Grid
        mt="1.5rem"
        rowGap={1}
        columnGap={8}
        templateColumns="1fr fit-content(100%) fit-content(100%) fit-content(100%)"
        fontSize="sm"
      >
        <Text fontSize="md" fontWeight="bold">
          Coach
        </Text>
        <Text fontSize="md" fontWeight="bold" justifySelf="flex-end">
          Billed Hrs
        </Text>
        <Text fontSize="md" fontWeight="bold" justifySelf="flex-end">
          Unbilled Hrs
        </Text>
        <Text fontSize="md" fontWeight="bold">
          Subtotal
        </Text>
        <GridItem colSpan={4}>
          <Divider />
        </GridItem>
        {line_items
          .sort((a, b) => b.coached_time - a.coached_time)
          .map(coach => (
            <React.Fragment key={coach.name}>
              <Text>{coach.name}</Text>
              <Text justifySelf="flex-end">
                {coach.billed_time ?? coach.coached_time}
              </Text>
              <Text justifySelf="flex-end">{coach.unbilled_time ?? 0}</Text>
              <Text justifySelf="flex-end">
                {prettyMoney((coach.billed_time ?? coach.coached_time) * rate)}
              </Text>
            </React.Fragment>
          ))}
        <GridItem colSpan={4}>
          <Divider />
        </GridItem>
        <Spacer />
        <Text fontSize="md" fontWeight="bold" justifySelf="flex-end">
          Amount Due:
        </Text>
        <Text fontSize="md" fontWeight="bold" justifySelf="flex-end">
          {prettyMoney(amount)}
        </Text>
      </Grid>
      <Text marginTop="3rem" fontSize="xs">
        Please remit payment through the Charisma Portal using the
        &quot;Pay&quot; button. This will redirect you to the UT Dallas - Center
        for BrainHealth payment page where you may pay either with a
        credit/debit card or with an ACH transfer.
        <br />
        <br />
        For billing questions, please call: 972.883.3312
        <br />
        or contact Doris.Nguyen@utdallas.edu
      </Text>
    </Page>
  </Container>
);

export default PDF;
